import type { VatEditorVm, VatPreviewVm, VatProblemVm, VatRateCountVm, VatReportVm, VatSourceVm } from '@krp/platform-ui'
import type { VatApplyResult, VatGroup, VatItemResult, VatPreview, VatSummary } from '@krp/resource-onec-ui'

import type { QueryLike } from '../platform/vatSettingsModel'

/** Чистая сборка vm ставки НДС базы 1С: из ответов службы и намерения человека. Без React, без store. */

/** Группы в порядке обхода дерева; вложенность — отступом в подписи (выпадающий список плоский). */
export function groupOptions(groups: readonly VatGroup[]): { value: string; label: string }[] {
  const byParent = new Map<string | null, VatGroup[]>()
  const known = new Set(groups.map((group) => group.key))
  for (const group of groups) {
    // Родитель без товаров в сводку не попал — группа поднимается на корень, а не пропадает.
    const parent = group.parentKey !== null && known.has(group.parentKey) ? group.parentKey : null
    byParent.set(parent, [...(byParent.get(parent) ?? []), group])
  }
  const result: { value: string; label: string }[] = [{ value: '', label: 'Вся номенклатура' }]
  const seen = new Set<string>()
  const walk = (parent: string | null, depth: number) => {
    for (const group of byParent.get(parent) ?? []) {
      if (seen.has(group.key)) continue
      seen.add(group.key)
      const without = group.withoutVat > 0 ? `, без ставки ${group.withoutVat}` : ''
      result.push({ value: group.key, label: `${'— '.repeat(depth)}${group.name} (${group.total}${without})` })
      walk(group.key, depth + 1)
    }
  }
  walk(null, 0)
  return result
}

export const rateCounts = (summary: VatSummary): VatRateCountVm[] =>
  summary.byRate.map((rate) => ({ key: rate.code ?? rate.name, label: rate.name, count: rate.count }))

export interface OneCEditorState {
  rateCode: string
  groupKey: string
  stage: VatEditorVm['stage']
  preview: VatPreview | null
  confirmText: string
  progress: { done: number; total: number } | null
  report: VatReportVm | null
  error: string | null
}

export const INITIAL_EDITOR: OneCEditorState = {
  rateCode: '',
  groupKey: '',
  stage: 'idle',
  preview: null,
  confirmText: '',
  progress: null,
  report: null,
  error: null,
}

export type OneCEditorAction =
  | { type: 'rate'; code: string }
  | { type: 'group'; key: string }
  | { type: 'previewing' }
  | { type: 'previewed'; preview: VatPreview }
  | { type: 'failed'; message: string }
  | { type: 'confirm'; text: string }
  | { type: 'running'; total: number }
  | { type: 'progress'; done: number }
  | { type: 'done'; report: VatReportVm }
  | { type: 'back' }

export function editorReducer(state: OneCEditorState, action: OneCEditorAction): OneCEditorState {
  switch (action.type) {
    case 'rate':
      return { ...state, rateCode: action.code, error: null }
    case 'group':
      return { ...state, groupKey: action.key, error: null }
    case 'previewing':
      return { ...state, stage: 'previewing', error: null }
    case 'previewed':
      return { ...state, stage: 'preview', preview: action.preview, confirmText: '', error: null }
    case 'failed':
      // Ошибка предпросмотра возвращает к выбору; ошибка записи отчётом не заменяется (её несёт report).
      return { ...state, stage: state.stage === 'previewing' ? 'idle' : state.stage, error: action.message }
    case 'confirm':
      return { ...state, confirmText: action.text }
    case 'running':
      return { ...state, stage: 'running', progress: { done: 0, total: action.total }, error: null }
    case 'progress':
      return { ...state, progress: state.progress === null ? null : { ...state.progress, done: action.done } }
    case 'done':
      return { ...state, stage: 'done', report: action.report, progress: null, preview: null, confirmText: '' }
    case 'back':
      return { ...state, stage: 'idle', preview: null, report: null, confirmText: '', progress: null, error: null }
  }
}

const previewVm = (preview: VatPreview): VatPreviewVm => ({
  rateLabel: preview.rateName,
  scopeLabel: preview.groupName === null ? 'вся номенклатура' : `группа «${preview.groupName}» со вложенными`,
  inScope: preview.inScope,
  willChange: preview.willChange,
  alreadySet: preview.alreadySet,
  skipped: preview.skipped.map((skip) => ({ text: skip.text, count: skip.count })),
})

export function oneCEditorVm(summary: VatSummary, state: OneCEditorState): VatEditorVm {
  const preview = state.preview
  return {
    rateOptions: summary.rates.map((rate) => ({ value: rate.code, label: rate.name })),
    groupOptions: groupOptions(summary.groups),
    rateCode: state.rateCode,
    groupKey: state.groupKey,
    stage: state.stage,
    preview: preview === null ? null : previewVm(preview),
    confirmText: state.confirmText,
    confirmOk: preview !== null && preview.willChange > 0 && state.confirmText.trim() === String(preview.willChange),
    progress: state.progress,
    report: state.report,
    error: state.error,
  }
}

export function oneCSourceVm(args: {
  id: string
  title: string
  badge: string
  summary: VatSummary | null
  query: QueryLike
  editor: OneCEditorState
  canWrite: boolean
}): VatSourceVm {
  const { id, title, badge, summary, query, editor, canWrite } = args
  const base = { id, title, badge, rates: [] as VatRateCountVm[], total: null, withVat: null, withoutVat: null, editor: null }
  if (summary === null) {
    return query.isError
      ? { ...base, status: 'error', message: query.errorMessage, note: null }
      : { ...base, status: 'loading', message: null, note: null }
  }
  if (!summary.available) {
    return { ...base, status: 'no-data', message: summary.reason, note: null }
  }
  return {
    ...base,
    status: 'ready',
    message: null,
    total: summary.total,
    withVat: summary.withVat,
    withoutVat: summary.withoutVat,
    rates: rateCounts(summary),
    note:
      (summary.markedForDeletion > 0 ? `Помеченных на удаление в 1С не считаем: ${summary.markedForDeletion}. ` : '') +
      (canWrite ? '' : 'Менять ставку здесь вам не разрешено: нужно право изменять каталог в этой базе.'),
    editor: canWrite ? oneCEditorVm(summary, editor) : null,
  }
}


const OUTCOME_TEXT: Readonly<Record<string, string>> = {
  Failed: 'не записан',
  NotFound: 'товар не найден в 1С',
  Unknown: 'исход неизвестен',
  NotProcessed: 'не обработан',
}

/** Отчёт: счётчики и каждый проблемный товар. Не обработанные (после остановки) в список не раскладываются — их число в счётчике. */
export function reportOf(totals: { changed: number; alreadySet: number; failed: number; unknown: number; notProcessed: number }, items: readonly VatItemResult[], stopped: string | null): VatReportVm {
  const problems: VatProblemVm[] = items
    .filter((item) => item.outcome === 'Failed' || item.outcome === 'NotFound' || item.outcome === 'Unknown')
    .map((item) => ({ id: item.refKey, name: item.name, article: item.article, state: OUTCOME_TEXT[item.outcome] ?? item.outcome, message: item.message }))
  return {
    ...totals,
    complete: totals.failed === 0 && totals.unknown === 0 && totals.notProcessed === 0,
    stoppedBecause: stopped,
    problems,
  }
}

export interface RunTotals {
  changed: number
  alreadySet: number
  failed: number
  unknown: number
  notProcessed: number
}

export const EMPTY_TOTALS: RunTotals = { changed: 0, alreadySet: 0, failed: 0, unknown: 0, notProcessed: 0 }

export function addResult(totals: RunTotals, result: VatApplyResult): RunTotals {
  return {
    changed: totals.changed + result.changed,
    alreadySet: totals.alreadySet + result.alreadySet,
    failed: totals.failed + result.failed,
    unknown: totals.unknown + result.unknown,
    notProcessed: totals.notProcessed + result.notProcessed,
  }
}
